/**
 * BlockRenderer - 레지스트리 기반 블록 렌더러
 *
 * 통합 블록 시스템을 사용하여 모든 블록을 렌더링합니다.
 * switch-case 대신 레지스트리 패턴을 사용하여 확장성을 제공합니다.
 */

import {
  type BlockRendererContext,
  isBlockRegistered,
  renderBlockFromRegistry,
  UnknownBlock,
} from '@/shared/lib/block-registry.tsx';
import { cn } from '@/shared/lib/utils/classnames';
import type { Block } from '@/shared/types/blocks';

// 블록 컴포넌트들을 레지스트리에 등록 (side effect import)
import './blocks/register';

interface BlockRendererProps {
  /** 렌더링할 블록 배열 */
  blocks: Block[];
  /** 도구 클릭 핸들러 */
  onToolClick?: (toolId: string) => void;
  /** 래퍼 컨테이너 클래스명 */
  className?: string;
  /** 인라인 스타일 (gap 커스텀 등) */
  style?: React.CSSProperties;
  /** 개별 블록에 적용할 클래스명 */
  blockClassName?: string;
  /** 미등록 블록 시 폴백 표시 여부 */
  showUnknownBlocks?: boolean;
  /** flex 컨테이너 비활성화 (카드 내부 등 자연스러운 흐름이 필요할 때) */
  noFlex?: boolean;
  /** 연결된 YouTube 영상 ID (video-info + transcript 레이아웃용) */
  linkedVideoId?: string;
}

/**
 * 블록 배열을 렌더링하는 메인 컴포넌트
 *
 * @example
 * ```tsx
 * <BlockRenderer
 *   blocks={detailCard.contentBlocks}
 *   onToolClick={handleToolClick}
 * />
 * ```
 */
export function BlockRenderer({
  blocks,
  onToolClick,
  className,
  style,
  blockClassName,
  showUnknownBlocks = process.env.NODE_ENV === 'development',
  noFlex = false,
  linkedVideoId,
}: BlockRendererProps) {
  const context: BlockRendererContext = {
    onToolClick,
    linkedVideoId,
  };

  // blocks가 undefined/null이거나 배열이 아닌 경우 빈 배열로 처리
  const safeBlocks = Array.isArray(blocks) ? blocks : [];

  // noFlex: 카드 내부 등에서 자연스러운 흐름 레이아웃 사용
  const containerClass = noFlex ? className : cn('flex flex-col gap-10', className);

  return (
    <div className={containerClass} style={style}>
      {safeBlocks.map((block) => {
        // 레지스트리에서 컴포넌트 조회 및 렌더링
        const element = renderBlockFromRegistry(block, context);

        if (element) {
          return blockClassName ? (
            <div key={block.id} className={blockClassName}>
              {element}
            </div>
          ) : (
            element
          );
        }

        // 미등록 블록 처리
        if (showUnknownBlocks && !isBlockRegistered(block.type)) {
          return <UnknownBlock key={block.id} type={block.type} id={block.id} />;
        }

        return null;
      })}
    </div>
  );
}

/**
 * 단일 블록 렌더링 헬퍼
 *
 * 컨테이너 블록 내부에서 개별 블록을 렌더링할 때 사용
 */
export function renderBlock(block: Block, context?: BlockRendererContext): React.ReactNode {
  return renderBlockFromRegistry(block, context);
}
